import { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Loader2, Activity, AlertTriangle, Zap } from "lucide-react";

const ACTION_LABEL = {
  analyze: { es: "Análisis", en: "Analysis" },
  compare: { es: "Comparativo", en: "Compare" },
  stt: { es: "Transcripción", en: "Transcription" },
  briefing_actor: { es: "Briefing actor", en: "Actor briefing" },
  briefing_corpus: { es: "Briefing corpus", en: "Corpus briefing" },
  press_kit: { es: "Kit de prensa", en: "Press kit" },
  alerts_run: { es: "Alertas enviadas", en: "Alerts run" },
  alerts_preview: { es: "Preview alerta", en: "Alert preview" },
  export_pdf: { es: "Export PDF", en: "Export PDF" },
  export_md: { es: "Export Markdown", en: "Export MD" },
};

const BUCKET_LABEL = {
  analyses: { es: "Análisis / Audio / Press Kit", en: "Analyses / Audio / Press Kit" },
  compare: { es: "Comparativos", en: "Compare" },
  briefing: { es: "Briefings PDF", en: "PDF briefings" },
  alerts: { es: "Alertas email", en: "Email alerts" },
};

const MODEL_LABEL = {
  claude: "Claude Sonnet 4.5",
  openai: "GPT-5.2",
  gemini: "Gemini 3 Pro",
  "whisper-1": "Whisper",
};

function fmtCost(c) {
  if (c == null) return "—";
  if (c === 0) return "$0.00";
  return `$${Number(c).toFixed(4)}`;
}

function pct(used, limit) {
  if (!limit || limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

export default function Usage() {
  const { lang, t } = useLang();
  const [limits, setLimits] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [lim, sum, rec] = await Promise.all([
          api.get("/usage/limits"),
          api.get(`/usage/summary?days=${days}`),
          api.get("/usage/recent?limit=20"),
        ]);
        if (cancelled) return;
        setLimits(lim.data);
        setSummary(sum.data);
        setRecent(rec.data.items || []);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e.message || "load error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [days]);

  const maxDay = useMemo(() => {
    if (!summary?.by_day?.length) return 0;
    return Math.max(...summary.by_day.map((d) => d.count));
  }, [summary]);

  const titleEs = "Uso y coste";
  const titleEn = "Usage & cost";
  const eyebrowEs = "· TELEMETRÍA · USO POR USUARIO";
  const eyebrowEn = "· TELEMETRY · PER-USER USAGE";

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="usage-page">
      <Header />
      <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-10 md:py-16 space-y-10">
        {/* Hero */}
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#D90429] mb-4 flex items-center gap-3">
              <span className="w-8 h-px bg-[#D90429]" />
              {lang === "es" ? eyebrowEs : eyebrowEn}
            </div>
            <h1
              data-testid="usage-title"
              className="font-serif-display text-5xl md:text-6xl font-bold tracking-tighter leading-none mb-4"
            >
              {lang === "es" ? titleEs : titleEn}
            </h1>
            <p className="text-[#404040] text-lg max-w-2xl">
              {lang === "es"
                ? "Telemetría agregada de tus acciones, modelos y consumo diario. El coste mostrado es una estimación aproximada según los precios configurados, no una factura."
                : "Aggregated telemetry of your actions, models and daily consumption. The cost shown is an approximate estimate from configured prices — not a bill."}
            </p>
          </div>
          <div className="md:col-span-4 border border-[#171717] bg-white p-4">
            <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">
              {lang === "es" ? "Ventana" : "Window"}
            </label>
            <div className="flex gap-0 border border-[#0A0A0A]" data-testid="window-selector">
              {[1, 7, 30, 90].map((n) => (
                <button
                  key={n}
                  data-testid={`window-${n}`}
                  onClick={() => setDays(n)}
                  className={`flex-1 py-2 font-mono-tech text-xs uppercase tracking-widest border-r border-[#0A0A0A] last:border-r-0 ${
                    days === n ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F5F5F5]"
                  }`}
                >
                  {n}d
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading && (
          <div
            data-testid="usage-loading"
            className="font-mono-tech text-xs uppercase tracking-widest inline-flex items-center gap-2"
          >
            <Loader2 className="w-4 h-4 animate-spin" /> ···
          </div>
        )}

        {error && (
          <div
            data-testid="usage-error"
            className="border border-[#D90429] bg-white p-4 flex items-start gap-3"
          >
            <AlertTriangle className="w-4 h-4 text-[#D90429] mt-0.5" />
            <div className="font-mono-tech text-xs text-[#D90429]">{error}</div>
          </div>
        )}

        {/* Limits */}
        {limits && (
          <section data-testid="limits-section">
            <div className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#A3A3A3] mb-4 flex items-center gap-3">
              <Zap className="w-3.5 h-3.5 text-[#D90429]" />
              {lang === "es" ? "Límites diarios" : "Daily limits"} · {limits.date}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#171717] border border-[#171717]">
              {limits.limits.map((b) => {
                const p = pct(b.used_today, b.limit_daily);
                const danger = p >= 80;
                return (
                  <div
                    key={b.bucket}
                    data-testid={`limit-${b.bucket}`}
                    className="bg-white p-5 flex flex-col"
                  >
                    <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[#D90429] mb-2">
                      · {BUCKET_LABEL[b.bucket]?.[lang] ?? b.bucket}
                    </div>
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="font-serif-display text-4xl font-bold leading-none">
                        {b.used_today}
                      </span>
                      <span className="font-mono-tech text-[11px] tracking-widest text-[#A3A3A3]">
                        / {b.limit_daily ?? "∞"}
                      </span>
                    </div>
                    <div className="w-full h-1 bg-[#E5E5E5] mb-2">
                      <div
                        className={`h-full ${danger ? "bg-[#D90429]" : "bg-[#0A0A0A]"}`}
                        style={{ width: `${p}%` }}
                      />
                    </div>
                    <div className="font-mono-tech text-[10px] tracking-widest text-[#A3A3A3] uppercase">
                      {b.remaining == null
                        ? lang === "es"
                          ? "sin límite"
                          : "no limit"
                        : `${b.remaining} ${lang === "es" ? "restantes" : "remaining"}`}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Totals */}
        {summary && (
          <section data-testid="totals-section">
            <div className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#A3A3A3] mb-4 flex items-center gap-3">
              <Activity className="w-3.5 h-3.5 text-[#D90429]" />
              {lang === "es" ? `Últimos ${summary.days} días` : `Last ${summary.days} days`}
            </div>
            <div className="grid md:grid-cols-4 gap-px bg-[#171717] border border-[#171717]">
              <div className="bg-white p-5" data-testid="total-actions">
                <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3] mb-2">
                  {lang === "es" ? "Acciones" : "Actions"}
                </div>
                <div className="font-serif-display text-5xl font-bold leading-none">
                  {summary.totals.actions}
                </div>
              </div>
              <div className="bg-white p-5" data-testid="total-ok">
                <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3] mb-2">
                  OK
                </div>
                <div className="font-serif-display text-5xl font-bold leading-none">
                  {summary.totals.ok}
                </div>
              </div>
              <div className="bg-white p-5" data-testid="total-errors">
                <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3] mb-2">
                  {lang === "es" ? "Errores" : "Errors"}
                </div>
                <div className="flex items-baseline gap-2">
                  <span
                    data-testid="errors-fraction"
                    className="font-serif-display text-3xl font-bold leading-none text-[#D90429]"
                  >
                    {summary.totals.errors}
                    <span className="text-[#A3A3A3] font-serif-display text-xl">
                      {" / "}
                      {summary.totals.actions || 0}
                    </span>
                  </span>
                </div>
                <div
                  data-testid="error-rate"
                  className="font-mono-tech text-[10px] tracking-widest uppercase text-[#A3A3A3] mt-2"
                >
                  {((summary.totals.error_rate ?? 0) * 100).toFixed(1)}% ·{" "}
                  {summary.by_http_status && summary.by_http_status.length > 0 ? (
                    <span data-testid="http-status-breakdown">
                      {summary.by_http_status.map((s, i) => (
                        <span key={s.status} className="ml-1">
                          {i > 0 ? "·" : ""} {s.status}: {s.count}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span>—</span>
                  )}
                </div>
              </div>
              <div className="bg-white p-5" data-testid="total-cost">
                <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3] mb-2">
                  {lang === "es" ? "Coste estimado" : "Estimated cost"}
                </div>
                <div className="font-serif-display text-5xl font-bold leading-none">
                  {fmtCost(summary.totals.cost_estimated)}
                </div>
                <div className="font-mono-tech text-[9px] tracking-widest uppercase text-[#A3A3A3] mt-2">
                  {lang === "es" ? "aproximado · no facturado" : "approx · not billed"}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* By day chart */}
        {summary?.by_day?.length > 0 && (
          <section data-testid="byday-section">
            <div className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#A3A3A3] mb-4">
              · {lang === "es" ? "Actividad por día" : "Activity by day"}
            </div>
            <div className="border border-[#171717] bg-white p-6">
              <div className="flex items-end gap-1 h-40" data-testid="byday-chart">
                {summary.by_day.map((d) => {
                  const h = maxDay > 0 ? Math.max(2, Math.round((d.count / maxDay) * 100)) : 2;
                  return (
                    <div
                      key={d.key}
                      title={`${d.key} · ${d.count} · ${fmtCost(d.cost_estimated)}`}
                      className="flex-1 flex flex-col items-center gap-1"
                    >
                      <div className="font-mono-tech text-[9px] text-[#A3A3A3]">{d.count}</div>
                      <div
                        className="w-full bg-[#0A0A0A] hover:bg-[#D90429] transition-colors"
                        style={{ height: `${h}%` }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 font-mono-tech text-[9px] tracking-widest uppercase text-[#A3A3A3]">
                <span>{summary.by_day[0]?.key}</span>
                <span>{summary.by_day[summary.by_day.length - 1]?.key}</span>
              </div>
            </div>
          </section>
        )}

        {/* By action / By model */}
        {summary && (
          <div className="grid md:grid-cols-2 gap-px bg-[#171717] border border-[#171717]">
            <section className="bg-white p-6" data-testid="byaction-section">
              <div className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#A3A3A3] mb-4">
                · {lang === "es" ? "Por acción" : "By action"}
              </div>
              {summary.by_action.length === 0 ? (
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                  —
                </div>
              ) : (
                <table className="w-full font-mono-tech text-xs">
                  <thead>
                    <tr className="border-b border-[#E5E5E5]">
                      <th className="text-left py-2 tracking-widest uppercase text-[#A3A3A3]">
                        {lang === "es" ? "Acción" : "Action"}
                      </th>
                      <th className="text-right py-2 tracking-widest uppercase text-[#A3A3A3]">
                        N
                      </th>
                      <th className="text-right py-2 tracking-widest uppercase text-[#A3A3A3]">
                        OK
                      </th>
                      <th className="text-right py-2 tracking-widest uppercase text-[#A3A3A3]">
                        {lang === "es" ? "Coste est." : "Est. cost"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.by_action.map((a) => (
                      <tr
                        key={a.key}
                        data-testid={`action-row-${a.key}`}
                        className="border-b border-[#F5F5F5]"
                      >
                        <td className="py-2">{ACTION_LABEL[a.key]?.[lang] ?? a.key}</td>
                        <td className="text-right py-2">{a.count}</td>
                        <td className="text-right py-2">{a.ok}</td>
                        <td className="text-right py-2">{fmtCost(a.cost_estimated)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
            <section className="bg-white p-6" data-testid="bymodel-section">
              <div className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#A3A3A3] mb-4">
                · {lang === "es" ? "Por modelo" : "By model"}
              </div>
              {summary.by_model.length === 0 ? (
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                  —
                </div>
              ) : (
                <table className="w-full font-mono-tech text-xs">
                  <thead>
                    <tr className="border-b border-[#E5E5E5]">
                      <th className="text-left py-2 tracking-widest uppercase text-[#A3A3A3]">
                        {lang === "es" ? "Modelo" : "Model"}
                      </th>
                      <th className="text-right py-2 tracking-widest uppercase text-[#A3A3A3]">
                        N
                      </th>
                      <th className="text-right py-2 tracking-widest uppercase text-[#A3A3A3]">
                        OK
                      </th>
                      <th className="text-right py-2 tracking-widest uppercase text-[#A3A3A3]">
                        {lang === "es" ? "Coste est." : "Est. cost"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.by_model.map((a) => (
                      <tr
                        key={a.key}
                        data-testid={`model-row-${a.key}`}
                        className="border-b border-[#F5F5F5]"
                      >
                        <td className="py-2">{MODEL_LABEL[a.key] ?? a.key}</td>
                        <td className="text-right py-2">{a.count}</td>
                        <td className="text-right py-2">{a.ok}</td>
                        <td className="text-right py-2">{fmtCost(a.cost_estimated)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        )}

        {/* Recent */}
        {recent.length > 0 && (
          <section data-testid="recent-section">
            <div className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#A3A3A3] mb-4">
              · {lang === "es" ? "Eventos recientes" : "Recent events"}
            </div>
            <div className="border border-[#171717] bg-white overflow-x-auto">
              <table className="w-full font-mono-tech text-xs">
                <thead>
                  <tr className="border-b border-[#0A0A0A] bg-[#FAFAFA]">
                    <th className="text-left p-3 tracking-widest uppercase text-[#A3A3A3]">
                      {lang === "es" ? "Fecha" : "Date"}
                    </th>
                    <th className="text-left p-3 tracking-widest uppercase text-[#A3A3A3]">
                      {lang === "es" ? "Acción" : "Action"}
                    </th>
                    <th className="text-left p-3 tracking-widest uppercase text-[#A3A3A3]">
                      {lang === "es" ? "Modelo" : "Model"}
                    </th>
                    <th className="text-left p-3 tracking-widest uppercase text-[#A3A3A3]">
                      Status
                    </th>
                    <th className="text-right p-3 tracking-widest uppercase text-[#A3A3A3]">
                      {lang === "es" ? "Coste est." : "Est. cost"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`event-row-${r.id}`}
                      className="border-b border-[#F5F5F5]"
                    >
                      <td className="p-3 text-[#404040]">
                        {new Date(r.timestamp).toLocaleString()}
                      </td>
                      <td className="p-3">{ACTION_LABEL[r.action]?.[lang] ?? r.action}</td>
                      <td className="p-3">{r.model ? MODEL_LABEL[r.model] ?? r.model : "—"}</td>
                      <td className="p-3">
                        <span
                          className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                            r.status === "ok"
                              ? "bg-[#0A0A0A] text-white"
                              : "bg-[#D90429] text-white"
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="p-3 text-right">{fmtCost(r.cost_estimated)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="font-mono-tech text-[10px] tracking-widest uppercase text-[#A3A3A3] mt-3">
              {lang === "es"
                ? "Coste estimado · valor aproximado configurado por administración. No es coste facturado."
                : "Estimated cost · approximate value configured by admin. Not an actual bill."}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
